// Finance Tab entity + API-envelope types — the FE mirror of
// backend/src/models/finance.ts (entity shapes) plus request/response shapes
// for the finance endpoints the FE consumes (summary, purchase orders,
// invoices, and the per-contract finance setup singleton).
//
// Entity shapes below (PoLine, PurchaseOrder, Invoice, InvoiceCharge,
// FinanceSetup, RateItem, LineAdjustment, ...) are declared directly here,
// mirroring the backend's plain TS interfaces in finance.ts (without the Zod
// schemas — validation is a backend-only concern). Calc-derived types
// (LineBalance, PoPosition, Payables and its payable interfaces,
// InvoiceDerivation, ContractRollup, MilestoneRef/SowRef) live in
// ../utils/financeCalc.ts (the FE mirror of backend/src/models/
// financeCalc.ts) and are re-exported here, not redeclared, so there is a
// single source of truth for each.
//
// - PoLine.lineType is a collapsed three-way enum (standard/contingency/
//   other); milestone/SOW/rate-item associations are tag arrays
//   (milestoneIds/sowIds/rateItemIds) rather than single foreign keys, and
//   each line carries its own signed `adjustments` ledger (opening balances
//   and SAP true-ups) plus its own `gstApplies` flag.
// - PurchaseOrder and Invoice carry no `supplierName` — the supplier is
//   always the contract's own supplier, not per-PO/per-invoice data. GST is
//   decided per PoLine (`gstApplies`) rather than at the PO header, since a
//   single PO can mix GST-applicable and GST-free lines; invoice-level
//   `gstApplies` is unrelated and stays as-is.
// - InvoiceCharge carries optional `rateItemId`/`milestoneId`/`sowId`
//   coverage refs (at most one typically set) instead of a single generic
//   `payableId`, so the UI can trace and highlight what a charge is
//   billing against.
//
// Response shapes below are matched field-for-field against the actual
// handler bodies in backend/src/handlers/finance.ts — a wire-shape mismatch
// between frontend types and the real backend response is a documented
// incident class (blank-detail-page bug) rather than a hypothetical risk.
// Read that handler before changing anything here. In particular:
//   - GET .../finance/summary returns
//     `{ purchaseOrders, payables, contractRollup, rateCard }`, where each PO
//     has `lineBalances`/`position` attached by `poWithDerived`
//     (finance.ts:132-144, used at finance.ts:220-223). `contractRollup` is
//     every PO's approved limit against the contract's own headline value
//     (finance.ts:238-247); `rateCard` is the finance-setup singleton's
//     rate-card array (`[]` when no setup row exists).
//   - GET .../finance/purchase-orders (list) returns `{ purchaseOrders }`,
//     same `poWithDerived` shape per PO (finance.ts:560-565).
//   - GET .../finance/purchase-orders/{poId} (single) returns the PO
//     *bare* (no wrapper object) with `lineBalances`/`position` spread onto
//     it directly (finance.ts:505-511).
//   - POST/PUT purchase-orders return the bare stored PurchaseOrder with NO
//     derived fields attached — only GET routes call `poWithDerived`
//     (finance.ts:568-587, finance.ts:513-536). Do not assume
//     `PurchaseOrderWithDerived` on a create/update response.
//   - DELETE purchase-orders/{poId} returns `{ deleted: true }`
//     (finance.ts:538-554); the API client discards the body (`Promise<void>`),
//     matching the existing delete-method convention in services/api.ts.
//   - GET .../finance/invoices (list) returns `{ invoices }`, each entry the
//     stored invoice plus a `derived` key — `InvoiceDerivation | null`, null
//     when the invoice's PO can't be found (finance.ts:458-472). Supports an
//     optional `?status=` filter (finance.ts:461,465).
//   - GET .../finance/invoices/{invoiceId} returns the invoice *bare*, with
//     `derived` spread on top (finance.ts:377-385) — same
//     `InvoiceWithDerived` shape as one list entry, not wrapped further.
//   - POST .../finance/invoices returns 201 with the bare created Invoice,
//     `status: 'draft'` or `'review'` per the request body (defaults to
//     `'review'` when omitted) — approval is a separate gated action
//     (finance.ts:474-499, esp. finance.ts:489-494).
//   - PUT .../finance/invoices/{invoiceId} returns the bare updated Invoice,
//     no `derived` attached (finance.ts:387-440).
//   - DELETE .../finance/invoices/{invoiceId} returns `{ deleted: true }`
//     (finance.ts:442-452).
//   - POST .../invoices/{invoiceId}/approve returns the approved Invoice
//     spread with `email` (the same preview sent to finance, built by
//     `buildApprovalEmail`) and `warnings` — a string array carried on the
//     response for any warnings the approve action wants to surface. The
//     approve already committed regardless of `warnings`.
//   - POST .../invoices/{invoiceId}/query returns the bare queried Invoice
//     (finance.ts:346-374).
//   - GET .../finance/setup returns `{ setup }`, `null` when no setup row
//     exists yet for the contract (finance.ts:227-231).
//   - PUT .../finance/setup returns `{ setup }`, the full-replace stored
//     FinanceSetup (finance.ts:232-249).

import type {
  LineBalance,
  PoPosition,
  Payables,
  MilestonePayable,
  ProductPayable,
  SowPayable,
  OtherPayable,
  MilestoneRef,
  SowRef,
  InvoiceDerivation,
  ContractRollup,
} from "../utils/financeCalc";

export type {
  LineBalance,
  PoPosition,
  Payables,
  MilestonePayable,
  ProductPayable,
  SowPayable,
  OtherPayable,
  MilestoneRef,
  SowRef,
  InvoiceDerivation,
  ContractRollup,
};

// ── entity shapes (mirrors backend/src/models/finance.ts) ─────────────────

export type PoLineType = "standard" | "contingency" | "other";

/** The three PO line types, in schema order — mirrors PO_LINE_TYPES in
 *  backend/src/models/finance.ts. Used to drive the line-type selector. */
export const PO_LINE_TYPES: readonly PoLineType[] = ["standard", "contingency", "other"];

// A manual ledger entry on a PO line: existing/opening balance (positive) or a
// SAP reconciliation true-up (either sign). `amount` is SIGNED.
export interface LineAdjustment {
  id: string;
  amount: number;
  label?: string;
  note?: string;
  effectiveDate?: string;
}

export interface PoLine {
  id: string;
  lineNumber: string;
  description: string;
  lineType: PoLineType;
  gstApplies: boolean;
  unitPrice?: number;
  quantity?: number;
  lineTotal: number;
  rateItemIds: string[];
  milestoneIds: string[];
  sowIds: string[];
  adjustments: LineAdjustment[];
}

export type PurchaseOrderStatus = "open" | "closed";

export interface PurchaseOrder {
  id: string;
  organisationId: string;
  contractId: string;
  poNumber: string;
  description?: string;
  totalValue: number;
  status: PurchaseOrderStatus;
  issuedDate?: string;
  lines: PoLine[];
  version: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface ChargeSplit {
  poLineId: string;
  amount: number;
  /** Operator-entered row quantity for multi-split rated charges. */
  qty?: number;
}

export interface InvoiceCharge {
  id: string;
  rateItemId?: string;
  milestoneId?: string;
  sowId?: string;
  label: string;
  expectedAmount?: number | null;
  qty?: number;
  splits: ChargeSplit[];
}

export type InvoiceStatus = "draft" | "review" | "approved" | "queried";

/** The approval email preview — byte-identical to what was actually sent. */
export interface ApprovalEmailPreview {
  to: string;
  subject: string;
  body: string;
  bodyHtml?: string;
}

/** A since-reverted approval, appended by the backend on revert. */
export interface ApprovalHistoryEntry {
  approvedAt: string;
  approvedBy: string;
  approvedByName?: string;
  approvedOnBehalf?: { name: string; receivedAt: string };
  approvalComment?: string;
  // Self-contained about what was approved, so a reverted invoice remains
  // freely editable afterwards without the entry pointing at mutable fields.
  invoiceNumber: string;
  totalAmount: number;
  approvalEmail?: ApprovalEmailPreview;
  revertedAt: string;
  revertedBy: string;
  revertReason?: string;
}

/** POST .../approve request body — mirrors invoiceApproveSchema. */
export interface ApproveInvoiceInput {
  comment?: string;
  onBehalf?: { name: string; receivedAt: string };
}

export interface Invoice {
  id: string;
  organisationId: string;
  contractId: string;
  poId: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: number;
  gstApplies?: boolean;
  status: InvoiceStatus;
  approvedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvalComment?: string;
  approvedOnBehalf?: { name: string; receivedAt: string };
  approvalEmail?: ApprovalEmailPreview;
  approvalHistory?: ApprovalHistoryEntry[];
  notes?: string;
  /** Link to the invoice's source document in SharePoint (validated https,
   *  sharepoint.com/*.sharepoint.com only — see the backend's isSharePointUrl). */
  sharePointUrl?: string;
  charges: InvoiceCharge[];
  version: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

// A catalogue entry for a contract product/service (contract rate card,
// embedded on FinanceSetup). `unitPrice: null` means the price is variable
// (entered fresh each invoice).
export interface RateItem {
  id: string;
  item: string;
  description?: string;
  unit: string;
  unitPrice: number | null;
  gstApplies: boolean;
}

export interface FinanceSetup {
  organisationId: string;
  contractId: string;
  financeEmail?: string;
  rateCard: RateItem[];
  version: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/**
 * A stored PurchaseOrder with the derived per-line balances + position
 * rollup attached — the shape every GET PO route (list/single/summary)
 * returns. Backend: `poWithDerived` in backend/src/handlers/finance.ts.
 */
export interface PurchaseOrderWithDerived extends PurchaseOrder {
  lineBalances: Record<string, LineBalance>;
  position: PoPosition;
}

/** GET .../finance/summary response body. */
export interface FinanceSummary {
  purchaseOrders: PurchaseOrderWithDerived[];
  payables: Payables;
  /** Every PO's approved limit against the contract's own headline value —
   *  Task 1.7's computeContractRollup (finance.ts:238-247). */
  contractRollup: ContractRollup;
  /** The contract's rate-card catalogue, straight off the finance setup
   *  singleton (`[]` when no setup row exists yet) — sourced here so the
   *  Finance tab's tag-option lists don't need a second setup fetch. */
  rateCard: RateItem[];
}

/** GET .../finance/purchase-orders response body (list envelope). */
export interface PurchaseOrderListResponse {
  purchaseOrders: PurchaseOrderWithDerived[];
}

// ─── Request payloads ────────────────────────────────────────────────────
// Mirror purchaseOrderCreateSchema / purchaseOrderUpdateSchema in
// backend/src/models/finance.ts. `id` is optional on a line on input the
// same way the backend accepts it (round-trip vs brand-new — the repository
// assigns a ulid when absent).

export interface LineAdjustmentInput {
  id?: string;
  amount: number;
  label?: string;
  note?: string;
  effectiveDate?: string;
}

export interface PoLineInput {
  id?: string;
  lineNumber: string;
  description: string;
  lineType: PoLineType;
  gstApplies: boolean;
  unitPrice?: number;
  quantity?: number;
  lineTotal: number;
  rateItemIds: string[];
  milestoneIds: string[];
  sowIds: string[];
  adjustments: LineAdjustmentInput[];
}

export interface CreatePurchaseOrderInput {
  poNumber: string;
  description?: string;
  totalValue: number;
  status?: PurchaseOrderStatus;
  issuedDate?: string;
  lines: PoLineInput[];
}

/** PUT body — every create field optional plus the optimistic-lock `version`. */
export type UpdatePurchaseOrderInput = Partial<CreatePurchaseOrderInput> & {
  version: number;
};

// ─── Invoice response envelopes ──────────────────────────────────────────

/**
 * A stored Invoice with its in-context derivation attached — `null` when the
 * invoice's PO can't be found. The shape every GET invoice route
 * (list/single) returns. Backend: finance.ts:458-472 (list),
 * finance.ts:377-385 (single).
 */
export interface InvoiceWithDerived extends Invoice {
  derived: InvoiceDerivation | null;
}

/** GET .../finance/invoices response body (list envelope, supports `?status=`). */
export interface InvoiceListResponse {
  invoices: InvoiceWithDerived[];
}

/** One row of GET /manage/finance/invoices — the org-wide slim projection
 *  feeding the global record search (no notes/charges/approval detail). */
export interface OrgWideInvoice {
  id: string;
  contractId: string;
  poId: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  totalAmount: number;
  invoiceDate: string;
  chargeLabels: string[];
}

/** GET /manage/finance/invoices response body. */
export interface OrgWideInvoiceListResponse {
  invoices: OrgWideInvoice[];
}

/**
 * POST .../invoices/{invoiceId}/approve response body — the approved Invoice
 * plus the sent (or attempted) approval email preview and any warnings.
 * The approve already committed regardless of `warnings` — see
 * finance.ts:314-329.
 */
export interface ApproveInvoiceResponse extends Invoice {
  email: ApprovalEmailPreview;
  warnings: string[];
}

/** GET / PUT .../finance/setup response body — `setup` is null until the
 *  first PUT for a contract. */
export interface SetupResponse {
  setup: FinanceSetup | null;
}

// ─── Invoice / setup request payloads ────────────────────────────────────
// Mirror invoiceCreateSchema / invoiceUpdateSchema / financeSetupPutSchema in
// backend/src/models/finance.ts. A charge's `id` is optional on input (round-
// trip vs brand-new — the repository assigns a ulid when absent), same
// pattern as `PoLineInput` above. Splits carry no id of their own.

export interface InvoiceChargeInput {
  id?: string;
  rateItemId?: string;
  milestoneId?: string;
  sowId?: string;
  label: string;
  expectedAmount?: number | null;
  qty?: number;
  splits: ChargeSplit[];
}

export interface CreateInvoiceInput {
  poId: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: number;
  gstApplies?: boolean;
  notes?: string;
  sharePointUrl?: string;
  charges: InvoiceChargeInput[];
  /** 'draft' saves without the review-queue charge requirements; omitted or
   *  'review' enters the queue. On update, 'review' promotes a draft. */
  status?: "draft" | "review";
}

/** PUT body — every create field optional plus the optimistic-lock `version`.
 *  `sharePointUrl: null` explicitly clears the stored link (REMOVE). Omit the
 *  key from the Partial<CreateInvoiceInput> half before re-adding it below —
 *  intersecting `string | undefined` with `string | null` would otherwise
 *  collapse to `string | undefined`, silently dropping the `null` clear. */
export type UpdateInvoiceInput = Omit<Partial<CreateInvoiceInput>, "sharePointUrl"> & {
  version: number;
  sharePointUrl?: string | null;
};

/** PUT .../finance/setup body — a full-replace document, no version lock.
 *  `milestoneMappings`/`standardPricing` are gone — PO-line milestone/rate
 *  tags supersede them. */
export interface FinanceSetupPutInput {
  financeEmail: string;
  rateCard: RateItem[];
}
